import { useState } from 'react';
import { AppNordModal } from '@/components/ui/AppNordModal';
import { Button } from '@/components/ui/button';
import { Appointment } from '@/types/appointment-types';
import { useAppointments } from '@/hooks/use-appointments';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';

export interface CancelAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: Appointment & {
    doctor?: {
      id: string;
      firstName: string;
      lastName: string;
      specialty?: string;
    };
  };
}

export function CancelAppointmentModal({
  isOpen,
  onClose,
  appointment,
}: CancelAppointmentModalProps) {
  const [cancellationReason, setCancellationReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { cancelAppointment } = useAppointments();
  const { toast } = useToast();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await cancelAppointment(appointment.id);
      
      toast({
        title: "Success",
        description: "Appointment successfully cancelled",
      });
      
      onClose();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast({
        title: "Error",
        description: "Failed to cancel appointment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const appointmentDate = new Date(appointment.startTime);
  const formattedDate = format(appointmentDate, 'EEEE, MMMM d, yyyy');
  const formattedTime = format(appointmentDate, 'h:mm a');

  return (
    <AppNordModal
      isOpen={isOpen}
      onClose={onClose}
      title="Cancel Appointment"
    >
      <div className="space-y-4">
        <div className="p-4 bg-red-50 rounded-md">
          <p className="text-sm text-red-600">
            Are you sure you want to cancel your appointment with{' '}
            <span className="font-medium">
              Dr. {appointment.doctor?.firstName} {appointment.doctor?.lastName}
            </span>{' '}
            on {formattedDate} at {formattedTime}?
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor="cancellation-reason" className="text-sm font-medium">
            Reason for cancellation (optional)
          </label>
          <textarea
            id="cancellation-reason"
            className="w-full border rounded-md p-2 h-20 text-sm"
            placeholder="Please provide a reason for cancellation..."
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
          />
        </div>

        <div className="pt-2">
          <p className="text-xs text-gray-500">
            Note: Our cancellation policy requires at least 24 hours notice to avoid a cancellation fee.
            Please refer to our policy for more details.
          </p>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Keep Appointment
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Cancelling...' : 'Cancel Appointment'}
          </Button>
        </div>
      </div>
    </AppNordModal>
  );
}

// Add default export to ensure compatibility
export default CancelAppointmentModal; 